import {
  Component,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router, RouterLinkWithHref, RouterOutlet } from '@angular/router';
import { BetaBannerComponent } from '@atoms/beta-banner/beta-banner.component';
import { EduButtonComponent } from '@atoms/button/button.component';
import { EduLoaderComponent } from '@atoms/loader/loader.component';
import { MobileMenuComponent } from '@compound/menus/mobile-menu/mobile-menu.component';
import { item, MenuContent, section } from '@compound/menus/types';
import { SharedCommonModule } from '@shared/common.module';
import { Icons } from '@atoms/icon/icon.component';
import { EduSidebarComponent } from '@compound/menus/sidebar/sidebar.component';
import { EduDialogService } from '@shared/dialogs/dialog.service';
import { ClientSubscriptionFlag } from '@shared/enums/client-subscription-flag';
import {
  ClientPermission,
  OwnPermission,
  WebinarPermission,
  WebinarTemplatePermission,
} from '@shared/enums/permission.enum';
import { UserResourceService } from '@shared/resources/user-resource.service';
import { AuthService } from '@shared/services/auth.service';
import { CustomizationMonetizationSyncService } from '@shared/services/customization-monetization-sync.service';
import { FileTransferOverlayService } from '@shared/services/file-transfer-overlay.service';
import { FLAGS } from '@shared/services/growthbook/flags';
import { GrowthBookService } from '@shared/services/growthbook/growth-book.service';
import { InitialLoadingService } from '@shared/services/initial-loading.service';
import { LayoutService } from '@shared/services/layout.service';
import { EduNewsService, NEWS } from '@shared/services/news.service';
import { PosthogService } from '@shared/services/posthog.service';

@Component({
  templateUrl: './internal.layout.html',
  imports: [
    SharedCommonModule,
    EduSidebarComponent,
    RouterOutlet,
    MobileMenuComponent,
    BetaBannerComponent,
    EduLoaderComponent,
    EduButtonComponent,
    RouterLinkWithHref,
  ],
})
export class InternalComponent implements OnDestroy {
  public router = inject(Router);
  public authService = inject(AuthService);
  public userResourceService = inject(UserResourceService);
  public growthBookService = inject(GrowthBookService);
  public initialLoadingService = inject(InitialLoadingService);
  private dialog = inject(EduDialogService);
  private news = inject(EduNewsService);
  private vcr = inject(ViewContainerRef);
  private layoutService = inject(LayoutService);
  // unused, but needed to initialize these services for the internal routes
  private posthogService = inject(PosthogService);
  private fileTransferOverlay = inject(FileTransferOverlayService);
  /** Instantiated here so the monetization-dependent defaults only sync for signed-in users. */
  private customizationSync = inject(CustomizationMonetizationSyncService);

  public createWebinarCTAIcon = Icons.CREATE;
  private mobileMenuContainerRef = viewChild<ElementRef<HTMLDivElement>>('mobileMenuContainer');

  constructor() {
    const stop = effect(() => {
      const ref = this.mobileMenuContainerRef();
      if (!ref) return;
      this.layoutService.mobileMenuContainerRef.set(ref);

      if (this.news.isNewsVisible()) {
        this.dialog.news(this.vcr, NEWS).then(() => {
          this.news.acknowledgeNews();
        });
      }

      stop.destroy();
    });
  }

  ngOnDestroy(): void {
    this.layoutService.mobileMenuContainerRef.set(null);
  }

  public sidebarMenuItems = signal<MenuContent[]>([
    item({
      label: 'routes.internal.dashboard.title',
      icon: Icons.DASHBOARD,
      route: '/',
      isActiveMatchOptions: {
        paths: 'exact',
      },
    }),
    section(
      [
        item({
          label: 'routes.internal.webinars.title',
          icon: Icons.WEBINARS,
          route: '/webinars',
          requiredFeatureFlags: [FLAGS.ENABLE_WEBINAR_MANAGEMENT_UI],
          checkedPermissions: [WebinarPermission.View, OwnPermission.WebinarViewOwn],
        }),
        item({
          label: 'routes.internal.recordings.title',
          icon: Icons.RECORDING,
          route: '/recordings',
          requiredFeatureFlags: [FLAGS.ENABLE_RECORDING_MANAGEMENT_UI],
          checkedPermissions: [WebinarPermission.View, OwnPermission.WebinarViewOwn],
        }),
        item({
          label: 'routes.internal.participants.title',
          icon: Icons.PARTICIPANTS,
          route: '/participants',
          requiredFeatureFlags: [FLAGS.ENABLE_PARTICIPANTS_UI],
          checkedPermissions: [WebinarPermission.View, OwnPermission.WebinarViewOwn],
        }),
        item({
          label: 'routes.internal.webinarTemplates.title',
          icon: Icons.WEBINAR_TEMPLATE,
          requiredClientSubscriptionFeature: ClientSubscriptionFlag.WebinarTemplatesEnabled,
          requiredFeatureFlags: [FLAGS.ENABLE_WEBINAR_TEMPLATES_MANAGEMENT_UI],
          route: '/webinar-templates',
          checkedPermissions: [WebinarTemplatePermission.View],
        }),
        item({
          label: 'routes.internal.automations.title',
          icon: Icons.AUTOMATION,
          requiredClientSubscriptionFeature: ClientSubscriptionFlag.AutomationTemplatingEnabled,
          requiredFeatureFlags: [FLAGS.ENABLE_AUTOMATION_MANAGEMENT_UI],
          route: '/automations',
          checkedPermissions: [ClientPermission.Settings],
        }),
      ],
      {
        labelKey: 'routes.internal.sidebar.sections.events',
        isCollapsible: true,
      },
    ),
    section(
      [
        item({
          label: 'routes.internal.monetization.title',
          icon: Icons.MONETIZATION,
          route: '/monetization',
          requiredClientSubscriptionFeature: ClientSubscriptionFlag.PaidWebinarsEnabled,
          requiredFeatureFlags: [FLAGS.ENABLE_MONETIZATION_MANAGEMENT_UI],
          checkedPermissions: [ClientPermission.Settings],
        }),
        item({
          label: 'routes.internal.settings.title',
          icon: Icons.SETTINGS,
          route: '/settings',
          requiredFeatureFlags: [FLAGS.ENABLE_SETTINGS_MANAGEMENT_UI],
          checkedPermissions: [ClientPermission.Settings],
        }),
      ],
      {
        labelKey: 'routes.internal.sidebar.sections.administration',
        isCollapsible: true,
      },
    ),
  ]);
}
